// src/components/resources/tools/generators/AdMockup/YouTubeAd.tsx
'use client'

import { PhotoIcon } from '@heroicons/react/24/outline'
import { CheckBadgeIcon as CheckBadgeSolidIcon } from '@heroicons/react/24/solid'
import { Avatar } from './Avatar'
import type { AdState } from './types'

/**
 * YouTube skippable in-stream (pre-roll) ad. Real pre-roll renders the video
 * frame (16:9) with a yellow ad-progress bar, an "Ad" marker, the "Skip Ad"
 * button bottom-right, and a small bottom-left companion overlay with the
 * advertiser, headline, and a CTA. A verified check sits by the advertiser name.
 */
export function YouTubeAd({ state, creativeImage, avatarImage }: { state: AdState; creativeImage: string | null; avatarImage: string | null }): JSX.Element {
  return (
    <div className="overflow-hidden rounded-xl bg-black font-sans text-white shadow-md">
      {/* video frame (16:9) */}
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        {creativeImage ? (
          // eslint-disable-next-line @next/next/no-img-element -- user-uploaded data URL; next/image can't optimize data URIs and html2canvas needs the raw <img>
          <img src={creativeImage} alt={`${state.pageName} ad creative`} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-slate-800 to-black text-white/60">
            <PhotoIcon className="h-9 w-9" />
            <span className="text-xs">Upload your video frame (16:9)</span>
          </div>
        )}

        {/* companion overlay (bottom-left) */}
        <div className="absolute bottom-4 left-2 flex max-w-[78%] items-center gap-2 rounded-md bg-black/75 px-2 py-1.5">
          <Avatar name={state.pageName} image={avatarImage} size={28} />
          <div className="min-w-0">
            <div className="flex items-center gap-1 text-[12px] font-semibold leading-tight">
              <span className="truncate">{state.headline}</span>
              {state.verified && <CheckBadgeSolidIcon className="h-3.5 w-3.5 flex-shrink-0 text-white/80" aria-label="Verified" />}
            </div>
            <div className="truncate text-[11px] text-white/70">{state.displayUrl}</div>
          </div>
          <span className="ml-1 flex-shrink-0 rounded-sm bg-white/15 px-2 py-1 text-[11px] font-semibold">{state.cta}</span>
        </div>

        {/* skip button (bottom-right) — sits above the progress bar, with a clean
            skip-to-end glyph (▷ + bar, no overlap). */}
        <div className="absolute bottom-3 right-0 flex items-center gap-1.5 border border-white/30 bg-black/70 px-3 py-1.5 text-[13px] font-medium">
          Skip Ad
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
            <path d="M3 2.5v11l7-5.5-7-5.5z" />
            <rect x="11.5" y="2.5" width="1.8" height="11" rx="0.4" />
          </svg>
        </div>

        {/* ad marker + progress bar (bottom edge) */}
        <div className="absolute bottom-2 left-2">
          <span className="rounded-sm bg-[#FAC600] px-1 py-px text-[10px] font-bold text-black">Ad</span>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-white/25">
          <span className="block h-full w-1/3 bg-[#FAC600]" />
        </div>
      </div>

      {/* under-player advertiser line */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <Avatar name={state.pageName} image={avatarImage} size={32} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 text-[13px] font-semibold">
            <span className="truncate">{state.pageName}</span>
            {state.verified && <CheckBadgeSolidIcon className="h-3.5 w-3.5 flex-shrink-0 text-white/70" aria-label="Verified" />}
          </div>
          <div className="text-[11px] text-white/60">Sponsored · {state.displayUrl}</div>
        </div>
        <span className="flex-shrink-0 rounded-full bg-white px-3 py-1 text-[13px] font-semibold text-black">{state.cta}</span>
      </div>
    </div>
  )
}
